/**
 * Trigger Klaviyo Mystery Box popups for 3-month-milestone `test` only.
 *
 * Forms:
 *   - Y8tnPa  WC | Desktop - Mystery Box
 *   - X63EpL  WC | Mobile - Mystery Box
 *
 * Behavior:
 *   - Wait until variant is known (`qh_milestone_variant` or PostHog flag)
 *   - If `test` and not already seen → openForm 3s after page load (any page)
 *   - Mark seen so we don't re-show across pages/refreshes
 *
 * Klaviyo setup required:
 *   Display timing → "Only show on custom trigger" for both forms.
 *
 * TODO(cleanup): Remove this temporary experiment bridge after 3-month-milestone ends.
 *   - Delete assets/klaviyo-milestone-form-gate.js
 *   - Delete snippets/klaviyo-milestone-form-gate.liquid
 *   - Remove `{% render 'klaviyo-milestone-form-gate' %}` from layouts
 *   - Remove `qh-milestone-variant-ready` dispatch from analytics-posthog-core.js
 *     (setCachedMilestoneVariant) if nothing else listens
 *   - Clear related localStorage keys: qh_milestone_variant, qh_mystery_box_form_seen
 *   - In Klaviyo, retire or retarget forms Y8tnPa / X63EpL (no longer custom-trigger-only)
 */
(function () {
  var VARIANT_KEY = 'qh_milestone_variant';
  var SEEN_KEY = 'qh_mystery_box_form_seen';
  var FLAG_KEY = '3-month-milestone';
  var FORM_DESKTOP = 'Y8tnPa';
  var FORM_MOBILE = 'X63EpL';
  var FORM_IDS = [FORM_DESKTOP, FORM_MOBILE];
  var DELAY_MS = 3000;
  var MOBILE_MAX_WIDTH = 767;

  var pageReadyAt = Date.now();
  var opened = false;
  var scheduled = false;
  var timerId = null;
  /** @type {'pending'|'test'|'control'} */
  var state = 'pending';

  function getCachedVariant() {
    try {
      var raw = window.localStorage.getItem(VARIANT_KEY);
      if (raw === 'test' || raw === 'control') return raw;
    } catch (e) {
      // ignore
    }
    return null;
  }

  function hasSeenForm() {
    try {
      return window.localStorage.getItem(SEEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markSeen() {
    try {
      window.localStorage.setItem(SEEN_KEY, '1');
    } catch (e) {
      // ignore
    }
  }

  function readFlagVariant() {
    var w = window.whiskyPosthog;
    if (!w || typeof w.getFeatureFlag !== 'function') return null;
    try {
      var flag = w.getFeatureFlag(FLAG_KEY);
      if (flag === 'test' || flag === true) return 'test';
      if (flag === 'control' || flag === false) return 'control';
    } catch (e) {
      // ignore
    }
    return null;
  }

  function isMobileViewport() {
    try {
      if (window.matchMedia) {
        return window.matchMedia('(max-width: ' + MOBILE_MAX_WIDTH + 'px)').matches;
      }
    } catch (e) {
      // ignore
    }
    return window.innerWidth <= MOBILE_MAX_WIDTH;
  }

  function targetFormId() {
    return isMobileViewport() ? FORM_MOBILE : FORM_DESKTOP;
  }

  function openForm(formId) {
    window._klOnsite = window._klOnsite || [];
    window._klOnsite.push(['openForm', formId]);
  }

  function clearTimer() {
    if (timerId != null) {
      window.clearTimeout(timerId);
      timerId = null;
    }
  }

  function scheduleOpen() {
    // Only schedule once per page; delay is measured from page load, not from flag resolve.
    if (scheduled || opened || state !== 'test' || hasSeenForm()) return;
    scheduled = true;
    var wait = Math.max(0, DELAY_MS - (Date.now() - pageReadyAt));
    timerId = window.setTimeout(function () {
      timerId = null;
      if (opened || state !== 'test' || hasSeenForm()) return;
      opened = true;
      openForm(targetFormId());
      // Treat open attempt as seen so refreshes/other pages don't re-trigger.
      markSeen();
    }, wait);
  }

  function applyState(next) {
    if (next !== 'test' && next !== 'control' && next !== 'pending') return;
    // Don't downgrade test → control after we've already decided test this page.
    if (state === 'test' && next === 'control') return;
    state = next;
    if (state === 'test') {
      scheduleOpen();
      return;
    }
    if (state === 'control') clearTimer();
  }

  function resolveFromCacheOrFlag() {
    var cached = getCachedVariant();
    if (cached) {
      applyState(cached);
      return true;
    }
    var flag = readFlagVariant();
    if (flag) {
      applyState(flag);
      return true;
    }
    applyState('pending');
    return false;
  }

  function tryResolveViaPosthog() {
    var w = window.whiskyPosthog;
    if (!w) return;

    // Prefer durable cache / live flag — avoid resolveMilestoneHeroVariant's
    // "has quiz session but no cache ⇒ control" shortcut, which is hero-specific.
    if (resolveFromCacheOrFlag()) return;

    if (typeof w.applyMilestoneUserForFlags === 'function') {
      w.applyMilestoneUserForFlags();
    }

    var finishFromFlag = function () {
      var flag = readFlagVariant();
      if (flag) {
        applyState(flag);
        return;
      }
      // Still unknown after reload — treat as non-test for this page.
      if (state === 'pending') applyState('control');
    };

    if (typeof w.onFeatureFlags === 'function' && typeof w.reloadFeatureFlags === 'function') {
      var reloadStarted = false;
      w.onFeatureFlags(function () {
        if (!reloadStarted) return;
        finishFromFlag();
      });
      reloadStarted = true;
      w.reloadFeatureFlags();
      window.setTimeout(function () {
        if (state === 'pending') finishFromFlag();
      }, 4000);
      return;
    }

    finishFromFlag();
  }

  function onVariantReady(e) {
    var variant =
      (e && e.detail && e.detail.variant) ||
      getCachedVariant() ||
      readFlagVariant();
    if (variant === 'test' || variant === 'control') applyState(variant);
  }

  function isGatedFormId(formId) {
    if (!formId) return false;
    var id = String(formId);
    for (var i = 0; i < FORM_IDS.length; i++) {
      if (FORM_IDS[i] === id) return true;
    }
    return false;
  }

  function onKlaviyoForms(e) {
    var detail = e && e.detail;
    if (!detail || !isGatedFormId(detail.formId)) return;
    if (
      detail.type === 'open' ||
      detail.type === 'close' ||
      detail.type === 'submit' ||
      detail.type === 'redirectedToUrl'
    ) {
      markSeen();
      opened = true;
      clearTimer();
    }
  }

  resolveFromCacheOrFlag();

  window.addEventListener('klaviyoForms', onKlaviyoForms);
  window.addEventListener('qh-milestone-variant-ready', onVariantReady);

  if (window.whiskyPosthog) {
    tryResolveViaPosthog();
  } else {
    window.addEventListener('whisky-posthog-ready', tryResolveViaPosthog, { once: true });
  }

  // If PostHog never resolves, do not open (stay non-test).
  window.setTimeout(function () {
    if (state === 'pending') applyState('control');
  }, 6000);

  window.__klaviyoMilestoneFormGate = {
    formIds: FORM_IDS.slice(),
    getState: function () {
      return state;
    },
    isAllowed: function () {
      return state === 'test';
    },
    hasSeen: hasSeenForm,
    refresh: resolveFromCacheOrFlag,
    openNow: function () {
      if (state !== 'test' || hasSeenForm()) return false;
      opened = true;
      scheduled = true;
      clearTimer();
      markSeen();
      openForm(targetFormId());
      return true;
    }
  };
})();
